<template>
  <div class="singlePost">
    <section class="single-post" @click="showFullImage">
      <!-- Blurred background image -->
      <div v-if="props.imgUrl!='https://i.postimg.cc/prBhfrST/cykExNN.png'" class="blur-bg" :style="{ backgroundImage: `url(${props.imgUrl})` }"></div>
      <!-- Sharp image on top -->
      <div class="sharp-bg" :style="{ backgroundImage: `url(${props.imgUrl})` }"></div>
    </section>
    
    <!-- Full image overlay modal -->
    <div v-if="isImageModalOpen" class="image-modal-overlay" @click="closeImageModal">
      <div class="image-modal-content" @click.stop>
        <button class="close-button" @click="closeImageModal">&times;</button>
        <img :src="props.imgUrl" alt="Full image" class="full-image" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  imgUrl: {
    required: false,
    default: 'https://i.postimg.cc/prBhfrST/cykExNN.png'
  }
})

const isImageModalOpen = ref(false)

const showFullImage = () => {
  if (props.imgUrl !== 'https://i.postimg.cc/prBhfrST/cykExNN.png') {
    isImageModalOpen.value = true
  }
}

const closeImageModal = () => {
  isImageModalOpen.value = false
}
</script>

<style scoped>
.single-post {
  position: relative;
  padding: 0;
  height: clamp(300px, 60vh, 600px);
  margin-top: clamp(60px, 8vh, 100px) !important;
  background-color: #e6e6e6;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
  border-radius: 0;
}

.blur-bg {
  position: absolute;
  top: 0; 
  left: 0; 
  right: 0; 
  bottom: 0;
  z-index: 1;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(2px) brightness(0.7);
  transform: scale(1.1);
  pointer-events: none;
}

.sharp-bg {
  position: absolute;
  top: 0; 
  left: 0; 
  right: 0; 
  bottom: 0;
  z-index: 2;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  /* Improved mask for better fade effect */
  -webkit-mask-image: radial-gradient(ellipse at center, black 60%, transparent 100%);
  mask-image: radial-gradient(ellipse at center, black 60%, transparent 100%);
}

.single-post > .landing {
  position: relative;
  z-index: 3;
}

/* Image Modal Styles */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.3s ease;
  backdrop-filter: blur(5px);
}

.image-modal-content {
  position: relative;
  width: 90vw;
  height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.close-button {
  position: absolute;
  top: -50px;
  right: -50px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 2.5rem;
  cursor: pointer;
  z-index: 10000;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.full-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  animation: zoomIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoomIn {
  from {
    transform: scale(0.7);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Responsive Design */
@media (min-width: 1200px) {
  .single-post {
    height: clamp(400px, 65vh, 700px);
  }
  
  .image-modal-content {
    width: 85vw;
    height: 85vh;
  }
}

@media (max-width: 1199px) and (min-width: 768px) {
  .single-post {
    height: clamp(350px, 60vh, 550px);
  }
  
  .image-modal-content {
    width: 90vw;
    height: 90vh;
  }
}

@media (max-width: 767px) and (min-width: 481px) {
  .single-post {
    height: clamp(250px, 50vh, 400px);
    margin-top: clamp(50px, 6vh, 80px) !important;
  }
  
  .close-button {
    top: -40px;
    right: -40px;
    width: 40px;
    height: 40px;
    font-size: 2rem;
  }
  
  .image-modal-content {
    width: 95vw;
    height: 95vh;
    padding: 15px;
  }
}

@media (max-width: 480px) {
  .single-post {
    height: clamp(200px, 40vh, 300px);
    margin-top: clamp(40px, 5vh, 60px) !important;
  }
  
  .blur-bg {
    filter: blur(1px) brightness(0.6);
  }
  
  .sharp-bg {
    -webkit-mask-image: radial-gradient(ellipse at center, black 70%, transparent 100%);
    mask-image: radial-gradient(ellipse at center, black 70%, transparent 100%);
  }
  
  .close-button {
    top: -35px;
    right: 10px;
    width: 35px;
    height: 35px;
    font-size: 1.8rem;
  }
  
  .image-modal-content {
    width: 98vw;
    height: 98vh;
    padding: 10px;
  }
  
  .full-image {
    border-radius: 8px;
  }
}

/* Landscape orientation adjustments */
@media (orientation: landscape) and (max-height: 500px) {
  .single-post {
    height: clamp(180px, 35vh, 250px);
  }
  
  .image-modal-content {
    height: 95vh;
  }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .blur-bg {
    filter: blur(1px) brightness(0.7);
  }
}

/* Reduced motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  .single-post,
  .close-button,
  .full-image {
    transition: none;
  }
  
  .image-modal-overlay,
  .full-image {
    animation: none;
  }
}
</style>